// ai-sidebar/components/markdown.tsx
// Compact markdown renderer tuned for the 392px sidebar: small headings, tight
// spacing, horizontally-scrollable GFM tables, inline bold/italic/code/links.
// Hand-rolled (no markdown dep in the repo) and kept deliberately small.
import React from "react";
import { BarChart3 } from "lucide-react";

/** Inline teal citation chip rendered from a [[cite:Label]] marker (#10). */
export function CiteChip({ label }: { label: string }) {
  return (
    <span
      className="ai-cite"
      data-ai-cite={label}
      title="Show the source for this"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        verticalAlign: "middle",
        marginLeft: 3,
        padding: "1px 6px 1px 5px",
        borderRadius: 6,
        fontSize: 10.5,
        fontWeight: 500,
        lineHeight: 1.3,
        background: "var(--alpha-teal-10)",
        border: "1px solid var(--alpha-teal-20)",
        color: "var(--ai-teal-text)",
      }}
    >
      <BarChart3 style={{ width: 11, height: 11, strokeWidth: 1.85 }} />
      {label}
    </span>
  );
}

/** Pull unique citation labels out of text (for the sources footnote, #9). */
export function extractCitations(text: string): string[] {
  const out: string[] = [];
  const re = /\[\[cite:([^\]]+)\]\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const label = m[1].trim();
    if (label && !out.includes(label)) out.push(label);
  }
  return out;
}

// ── Inline formatting: **bold**, *italic*, `code`, [text](url), [[cite:..]] ──
function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  // Order matters: code first (so ** inside code isn't parsed), then links, bold, italic.
  const re = /(\[\[cite:[^\]]+\]\])|(`[^`]+`)|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("[[cite:")) {
      out.push(<CiteChip key={`${keyBase}-${k++}`} label={tok.slice(7, -2).trim()} />);
    } else if (tok.startsWith("`")) {
      out.push(
        <code
          key={`${keyBase}-${k++}`}
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.88em",
            background: "var(--muted)",
            borderRadius: 4,
            padding: "1px 4px",
          }}
        >
          {tok.slice(1, -1)}
        </code>,
      );
    } else if (tok.startsWith("[")) {
      const mm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (mm) {
        out.push(
          <a
            key={`${keyBase}-${k++}`}
            href={mm[2]}
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--spectral-violet)", textDecoration: "underline" }}
          >
            {mm[1]}
          </a>,
        );
      } else out.push(tok);
    } else if (tok.startsWith("**")) {
      out.push(
        <strong key={`${keyBase}-${k++}`} className="font-semibold text-foreground">
          {tok.slice(2, -2)}
        </strong>,
      );
    } else {
      out.push(
        <em key={`${keyBase}-${k++}`}>{tok.slice(1, -1)}</em>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function isTableSep(line: string): boolean {
  return /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(line);
}
function splitRow(line: string): string[] {
  let t = line.trim();
  if (t.startsWith("|")) t = t.slice(1);
  if (t.endsWith("|")) t = t.slice(0, -1);
  return t.split("|").map((c) => c.trim());
}

// Hard house rule: never render an em dash. The model is instructed not to use
// them, but compliance isn't reliable, so we normalize deterministically at the
// single render chokepoint. Em dash (U+2014) -> spaced hyphen; surrounding
// whitespace is collapsed so " — " and "x—y" both become "x - y". En dashes
// (U+2013, used in numeric ranges) are intentionally left alone.
export function stripEmDashes(text: string): string {
  return text.replace(/\s*—\s*/g, " - ");
}

export function Markdown({ text }: { text: string }) {
  const lines = stripEmDashes(text).split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code
    if (line.trimStart().startsWith("```")) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trimStart().startsWith("```")) code.push(lines[i++]);
      i++;
      blocks.push(
        <pre
          key={key++}
          className="overflow-x-auto"
          style={{ background: "var(--muted)", borderRadius: 8, padding: 10, margin: "6px 0", fontSize: 12 }}
        >
          <code style={{ fontFamily: "var(--font-mono)" }}>{code.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    // GFM table
    if (line.includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") rows.push(splitRow(lines[i++]));
      blocks.push(
        <div key={key++} className="overflow-x-auto" style={{ margin: "8px 0" }}>
          <table className="w-full" style={{ borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                {header.map((h, hi) => (
                  <th
                    key={hi}
                    className="text-left font-semibold text-foreground"
                    style={{ borderBottom: "1px solid var(--border)", padding: "5px 8px", whiteSpace: "nowrap" }}
                  >
                    {renderInline(h, `th${key}-${hi}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri}>
                  {r.map((c, ci) => (
                    <td
                      key={ci}
                      className="text-muted-foreground align-top"
                      style={{ borderBottom: "1px solid var(--border)", padding: "5px 8px" }}
                    >
                      {renderInline(c, `td${key}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    // Headings
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const lvl = h[1].length;
      const size = lvl <= 2 ? 14 : 13;
      blocks.push(
        <div
          key={key++}
          className="font-semibold text-foreground"
          style={{ fontSize: size, margin: "10px 0 3px" }}
        >
          {renderInline(h[2], `h${key}`)}
        </div>,
      );
      i++;
      continue;
    }

    // Unordered list (consecutive - or * lines)
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*]\s+/, ""));
      blocks.push(
        <ul key={key++} className="space-y-1" style={{ margin: "4px 0", paddingLeft: 16, listStyle: "disc" }}>
          {items.map((it, ii) => (
            <li key={ii} className="text-foreground" style={{ fontSize: 13, lineHeight: 1.5 }}>
              {renderInline(it, `li${key}-${ii}`)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    // Ordered list
    if (/^\s*\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*\d+\.\s+/, ""));
      blocks.push(
        <ol key={key++} className="space-y-1" style={{ margin: "4px 0", paddingLeft: 18, listStyle: "decimal" }}>
          {items.map((it, ii) => (
            <li key={ii} className="text-foreground" style={{ fontSize: 13, lineHeight: 1.5 }}>
              {renderInline(it, `ol${key}-${ii}`)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    // Blank line
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph (gather consecutive non-empty, non-block lines)
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^(#{1,6})\s+/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !/^\s*\d+\.\s+/.test(lines[i]) &&
      !lines[i].trimStart().startsWith("```") &&
      !(lines[i].includes("|") && i + 1 < lines.length && isTableSep(lines[i + 1]))
    ) {
      para.push(lines[i++]);
    }
    blocks.push(
      <p key={key++} className="text-foreground" style={{ fontSize: 13, lineHeight: 1.55, margin: "4px 0" }}>
        {renderInline(para.join(" "), `p${key}`)}
      </p>,
    );
  }

  return <div className="ai-md">{blocks}</div>;
}
